import React from "react";
import { XP_DISCLAIMER } from "../../config/supernovaXp";
import { METRIC } from "./supernovaSummary";
import { ACTIVITY_EMPTY_NOTE, ACTIVITY_UNAVAILABLE_NOTE } from "./supernovaActivity";
import SupernovaOrb from "./SupernovaOrb";
import { fmtCount, fmtDate, fmtXpExact } from "./supernovaFormat";
import { ChainErrorCard, Skeleton } from "./SupernovaStates";

/**
 * "Recent activity" — the six most recent stake events we can actually see.
 *
 * NOTHING HERE IS COMPUTED. Every row comes from supernovaActivity, which
 * arranges the same lots the Active stakes table renders.
 *
 * IT SAYS WHAT IT ISN'T. TODO(backend, supervised): unstakes and closed lots are
 * only in the staking contract's event log, which this app doesn't read (V-1),
 * so this list is a PARTIAL history and the caption says exactly that. An
 * unlabelled partial feed would read as "this is everything you've done", which
 * for a member who has unstaked before would be wrong.
 *
 * COMPLIANCE: the XP on a row is what that lot has accrued so far — in-app
 * points with no monetary value. No return, yield, income, price, or dollar
 * figure appears here, and none may be added.
 *
 * @param {Object} props
 * @param {Object} props.activity      buildActivity() result
 * @param {() => void} [props.onRetry]
 * @param {boolean} [props.retrying]
 */
const SupernovaActivityFeed = ({ activity, onRetry, retrying = false }) => {
  const { status } = activity;

  if (status === METRIC.LOADING || status === METRIC.IDLE) return <ActivitySkeleton />;

  if (status === METRIC.ERROR) {
    return (
      <ChainErrorCard
        title="Couldn't read your stake activity"
        message={activity.message || "Couldn't read your stake activity from Arbitrum."}
        onRetry={onRetry}
        retrying={retrying}
      />
    );
  }

  if (status === METRIC.UNAVAILABLE) {
    return (
      <Shell>
        {/* TODO(backend, supervised): the lots behind this feed come from
            supernovaChain.getStaked, which reports itself unavailable until the
            staking contract's read descriptor is verified. An empty feed would
            read as "you've never staked" — which we don't know. */}
        <p className="text-body text-arc-ink-2" data-testid="supernova-activity-unavailable">
          {activity.note || ACTIVITY_UNAVAILABLE_NOTE}
        </p>
      </Shell>
    );
  }

  if (activity.events.length === 0) {
    return (
      <Shell>
        <p className="text-body text-arc-ink-3" data-testid="supernova-activity-empty">
          {ACTIVITY_EMPTY_NOTE}
        </p>
        <Footnote activity={activity} />
      </Shell>
    );
  }

  return (
    <Shell>
      <ul className="list-none p-0 m-0" data-testid="supernova-activity-list">
        {activity.events.map((event) => (
          <ActivityRow key={event.key} event={event} />
        ))}
      </ul>
      <Footnote activity={activity} />
    </Shell>
  );
};

/* ── One event ──────────────────────────────────────────────────────────── */

/**
 * "Staked 2 × Celestial Flare · 90-day lock" — what happened, what it has
 * accrued since, and when. The XP is labelled "accrued so far" on purpose: it is
 * not what the stake was "worth" on the day it opened, because nothing is
 * credited up front.
 */
const ActivityRow = ({ event }) => (
  <li
    className="flex items-baseline justify-between gap-2.5 flex-wrap py-2 border-b border-arc-line last:border-b-0"
    data-testid="supernova-activity-row"
  >
    <span className="flex items-baseline gap-2 min-w-0">
      {/* Decorative: the sentence beside it already names the token. */}
      <SupernovaOrb
        tokenId={event.tokenId}
        name={null}
        size={14}
        className="translate-y-[2px] flex-none"
      />
      <span className="text-[12.5px] font-semibold text-arc-ink-2 tnum">
        Staked {fmtCount(event.qty)} × {event.tokenName} · {event.lockName} lock
      </span>
    </span>
    <span className="text-[12.5px] text-arc-ink-3 tnum">
      {fmtXpExact(event.earnedXp)} XP accrued so far
    </span>
    <span className="text-[12.5px] text-arc-ink-4 tnum">{fmtDate(event.atMs)}</span>
  </li>
);

/* ── Chrome ─────────────────────────────────────────────────────────────── */

const Footnote = ({ activity }) => (
  <p className="text-caption text-arc-ink-4 mt-2.5 leading-[1.55]">
    {activity.note} {XP_DISCLAIMER}
    {activity.truncated
      ? ` Showing your ${fmtCount(activity.events.length)} most recent of ${fmtCount(
          activity.total
        )} open stakes.`
      : ""}
    {activity.unreadable
      ? ` ${fmtCount(activity.unreadable)} stake${
          activity.unreadable === 1 ? "" : "s"
        } couldn't be read, so ${activity.unreadable === 1 ? "it isn't" : "they aren't"} listed.`
      : ""}
  </p>
);

const Shell = ({ children }) => (
  <div className="bg-arc-card rounded-arc-card shadow-arc-sm ring-1 ring-arc-line px-[18px] py-3">
    {children}
  </div>
);

/** Skeleton rows per DESIGN_SYSTEM §7 — never a spinner, never a zero. */
const ActivitySkeleton = () => (
  <Shell>
    <span className="sr-only">Reading your recent stake activity…</span>
    <div className="space-y-3 py-1">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton radius="rounded-full" className="w-3.5 h-3.5 flex-none" />
          <Skeleton className="h-[12px] flex-1 max-w-[240px]" />
          <Skeleton className="h-[12px] w-[70px] flex-none" />
        </div>
      ))}
    </div>
  </Shell>
);

export default SupernovaActivityFeed;
